/**
 * useCoreEventAlerts — hook global que suena el chime del core cuando llega
 * un evento operativo nuevo que el operador tiene que atender.
 *
 * Reutiliza el mismo motor de audio que useProspectosPendientes (playChime,
 * con el priming del AudioContext ya montado por GlobalAlertBanner). Cada
 * fuente lleva su propio set de IDs conocidos para sonar SOLO cuando entra
 * uno realmente nuevo (no en la carga inicial ni en updates de filas viejas).
 *
 * Eventos cubiertos (los 5 que pidió Hugo):
 *   1. Crédito firmado listo para desembolsar  → solicitud_credito
 *   2. Verificación bancaria (comprobante subido) → cuenta_bancaria_asociado
 *   3. Comprobante de pago de asociación  → YA lo cubre useProspectosPendientes
 *      (asociado prospecto→pendiente); no se duplica aquí.
 *   4. Comprobante de pago (crédito/ahorro) → reporte_pago_asociado
 *   5. Solicitud de retiro → retiro
 *
 * Realtime + fallback de polling (20s) por si el canal se cae. Cada fuente
 * dispara playChime() + una browser Notification con su propio texto/URL.
 */
import { useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { playChime } from './useProspectosPendientes'

type Fuente = {
  key: string
  tabla: string
  /** Trae los IDs de las filas que representan el evento pendiente. */
  fetchIds: () => Promise<string[]>
  /** Texto de la notificación cuando entra 1 nuevo. */
  titulo: string
  cuerpo: string
  /** A dónde manda el click de la notificación. */
  url: string
}

const FUENTES: Fuente[] = [
  {
    key: 'credito-por-desembolsar',
    tabla: 'solicitud_credito',
    fetchIds: async () => {
      const { data } = await supabase
        .from('solicitud_credito')
        .select('id')
        .eq('estado', 'aprobada')
        .not('firma_timestamp', 'is', null)
      return (data ?? []).map((r: { id: string }) => r.id)
    },
    titulo: 'Crédito listo para desembolsar',
    cuerpo: 'Un asociado firmó su pagaré. Revisá Originación.',
    url: '/neocore/creditos/originacion',
  },
  {
    key: 'verificacion-bancaria',
    tabla: 'cuenta_bancaria_asociado',
    fetchIds: async () => {
      const { data } = await supabase
        .from('cuenta_bancaria_asociado')
        .select('id')
        .eq('estado', 'verificacion_enviada')
        .eq('activa', true)
      return (data ?? []).map((r: { id: string }) => r.id)
    },
    titulo: 'Nueva verificación bancaria',
    cuerpo: 'Un asociado subió su comprobante de cuenta.',
    url: '/neocore/verificaciones-bancarias',
  },
  {
    key: 'comprobante-pago',
    tabla: 'reporte_pago_asociado',
    fetchIds: async () => {
      const { data } = await supabase
        .from('reporte_pago_asociado')
        .select('id')
        .eq('estado', 'pendiente')
      return (data ?? []).map((r: { id: string }) => r.id)
    },
    titulo: 'Nuevo comprobante de pago',
    cuerpo: 'Hay un comprobante por conciliar.',
    url: '/neocore/reportes-pago',
  },
  {
    key: 'retiro-solicitado',
    tabla: 'retiro',
    fetchIds: async () => {
      const { data } = await supabase
        .from('retiro')
        .select('id')
        .eq('estado', 'solicitado')
      return (data ?? []).map((r: { id: string }) => r.id)
    },
    titulo: 'Nueva solicitud de retiro',
    cuerpo: 'Un asociado pidió un retiro.',
    url: '/neocore/retiros',
  },
]

const LOG = '[neocoop-core-alerts]'

function notificar(f: Fuente, nuevos: number) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  try {
    const n = new Notification(f.titulo, {
      body: f.cuerpo,
      icon: '/brand/logo-vertical.png',
      badge: '/brand/logo-vertical.png',
      tag: `neocoop-${f.key}`,
      requireInteraction: false,
    })
    n.onclick = () => {
      window.focus()
      window.location.href = f.url
      n.close()
    }
    void nuevos
  } catch {
    // silencioso — el sonido ya avisó
  }
}

export function useCoreEventAlerts() {
  // Un set de IDs conocidos + flag de inicializado por cada fuente.
  const knownRef = useRef<Map<string, Set<string>>>(new Map())
  const initRef = useRef<Map<string, boolean>>(new Map())

  useEffect(() => {
    let cancelado = false

    const refrescar = async (f: Fuente) => {
      let ids: string[]
      try {
        ids = await f.fetchIds()
      } catch (e) {
        console.warn(LOG, f.key, 'fetch error', e)
        return
      }
      if (cancelado) return

      const known = knownRef.current.get(f.key) ?? new Set<string>()
      const yaInit = initRef.current.get(f.key) ?? false

      let nuevos = 0
      if (yaInit) {
        for (const id of ids) if (!known.has(id)) nuevos++
      }

      knownRef.current.set(f.key, new Set(ids))
      initRef.current.set(f.key, true)

      if (nuevos > 0) {
        console.log(LOG, '🔔', f.key, '+', nuevos, 'nuevo(s)')
        playChime()
        notificar(f, nuevos)
      }
    }

    // Carga inicial (baseline, sin sonar) + suscripción realtime por fuente.
    const canales = FUENTES.map((f) => {
      void refrescar(f)
      return supabase
        .channel(`core-alert-${f.key}`)
        .on(
          'postgres_changes' as never,
          { event: 'INSERT', schema: 'public', table: f.tabla },
          () => { void refrescar(f) },
        )
        .on(
          'postgres_changes' as never,
          { event: 'UPDATE', schema: 'public', table: f.tabla },
          () => { void refrescar(f) },
        )
        .subscribe((status: string) => {
          console.log(LOG, f.key, 'realtime:', status)
        })
    })

    // Fallback de polling cada 20s por si algún canal no engancha.
    const poll = setInterval(() => {
      for (const f of FUENTES) void refrescar(f)
    }, 20_000)

    return () => {
      cancelado = true
      clearInterval(poll)
      for (const ch of canales) supabase.removeChannel(ch).catch(() => {})
    }
  }, [])
}
